import React, { useRef, useState } from 'react';
import { Tag } from 'antd';
import { PullRequestOutlined } from '@ant-design/icons';
import { TaskPullRequest } from '../../../types/Github';
import CustomTooltip from '../../../components/UI/Tooltip/CustomTooltip';

/**
 * The pull requests linked to this task.
 *
 * READ-ONLY on purpose. The server also accepts a manual link and an unlink on the same
 * resource, but a task's links are overwhelmingly created by the webhook pipeline from a
 * branch name or a PR body, and a control that lets someone attach a pull request by
 * hand is a different feature with its own failure modes (a URL from an unmapped
 * repository, a repository in another organization). This renders what the integration
 * produced and nothing else.
 *
 * Renders nothing at all when the task has no links. Every task in every organization
 * has this section otherwise, including the vast majority that will never see a pull
 * request, and an empty "Pull Requests" heading is worse than no heading.
 *
 * Placed in the same stack as Links and Attachments (TaskView), and styled to match:
 * a `-ml-[28px]` section heading with a count badge, then the rows.
 */
export interface TaskPullRequestsProps {
  pullRequests: TaskPullRequest[];
}

type StateBadge = { label: string; color: string };

/**
 * GitHub has no `merged` state: a merged pull request is `state: 'closed'` with
 * `merged: true`, and a draft is an `open` one. Reading `state` alone would report every
 * merge as "Closed" - the single most misleading thing this section could say.
 *
 * Order matters. Merged is checked before closed because a merged PR is both.
 */
const stateBadge = (pullRequest: TaskPullRequest['pullRequest']): StateBadge => {
  if (pullRequest.merged) return { label: 'Merged', color: 'purple' };
  if (pullRequest.state === 'closed') return { label: 'Closed', color: 'red' };
  if (pullRequest.draft) return { label: 'Draft', color: 'default' };
  return { label: 'Open', color: 'green' };
};

/**
 * The title tooltip shows only when the title is actually cut off. How much room it has
 * depends on the task view's width and on the repository name and author beside it, so a
 * character count cannot tell; it is measured on hover instead, which also makes it right
 * after any resize (#3225). Same pattern as the activity chips (`activityRenderer.tsx`).
 */
const PullRequestRow: React.FC<{ pullRequest: TaskPullRequest['pullRequest']; badge: StateBadge }> = ({
  pullRequest,
  badge,
}) => {
  const titleRef = useRef<HTMLSpanElement>(null);
  const [isTitleTruncated, setIsTitleTruncated] = useState(false);
  const measureTitle = () => {
    if (titleRef.current) setIsTitleTruncated(titleRef.current.scrollWidth > titleRef.current.clientWidth);
  };

  return (
    <CustomTooltip title={isTitleTruncated ? pullRequest.title : ''}>
      {/* `noopener,noreferrer` because the destination is a third-party origin. */}
      <a
        href={pullRequest.htmlUrl}
        target="_blank"
        rel="noopener noreferrer"
        onMouseEnter={measureTitle}
        className="box-border flex w-full items-center gap-2 rounded-md border border-solid border-neutral-300 px-3 py-1.5 no-underline hover:border-brand-taskonio-green"
      >
        <PullRequestOutlined className="shrink-0 text-neutral-900" />
        <span className="shrink-0 text-sm font-medium text-neutral-900">
          {pullRequest.repositoryFullName}#{pullRequest.number}
        </span>
        <span ref={titleRef} className="min-w-0 flex-1 truncate text-sm font-normal text-brand-grey-dark">
          {pullRequest.title}
        </span>
        <span className="shrink-0 text-xs text-neutral-900">@{pullRequest.authorLogin}</span>
        <Tag className="m-0 shrink-0" color={badge.color}>
          {badge.label}
        </Tag>
      </a>
    </CustomTooltip>
  );
};

export const TaskPullRequests: React.FC<TaskPullRequestsProps> = ({ pullRequests }) => {
  if (!pullRequests || pullRequests.length === 0) {
    return null;
  }

  // Newest first, by pull request number within a repository. There is no timestamp on
  // the DTO, and the number is a per-repository counter - so this orders each repository
  // sensibly and interleaves repositories arbitrarily, which is the honest ordering for
  // the data we have.
  const sorted = [...pullRequests].sort((a, b) => b.pullRequest.number - a.pullRequest.number);

  return (
    <div className="flex flex-col gap-2 mt-6">
      <div className="h-6 flex items-center gap-1 text-brand-grey-dark text-xs font-semibold">
        Pull Requests
        <div className="w-5 h-5 flex items-center justify-center bg-neutral-200 text-neutral-900 text-xs font-semibold rounded p-0.5">
          {sorted.length}
        </div>
      </div>

      <div className="flex flex-col gap-2">
        {sorted.map((link) => {
          const { pullRequest } = link;
          const badge = stateBadge(pullRequest);
          return <PullRequestRow key={link.linkId} pullRequest={pullRequest} badge={badge} />;
        })}
      </div>
    </div>
  );
};
